import { type LinearConfiguratorState } from "../redux/types";
import { getCsrfValue } from "../../../api/ApiHelper";
import { type LinearConfiguration } from "../models";
import { type LinearConfigurationDTO } from "../dtos";
import { transform } from "../linearDataTransformer";
import { resolveApiUrl } from "../../../utils/url/urlResolver";
import {
  type ConfiguratorApiCall,
  prepareCommonConfiguratorData,
  type UnitConfiguratorApiData,
} from "../../common/commonConfiguratorApi";
import { ConfiguratorType, getConfiguratorFamilyFromProductCode } from "../../ConfiguratorType";
import { type PriceInfo } from "../../../api/pna/PriceInfo";
import { FAKE_CSTIC_UNIT_LENGTH } from "~/Configurators/Linear/constants";
import { getMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import instance from "~/utils/i18n";
import { XML_TRANSLATION_SHEETS } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";

type LinearConfiguratorApiCall = ConfiguratorApiCall & Required<Pick<ConfiguratorApiCall, "linearConfiguratorData">>;

export function prepareCommonLinearData(
  linearState: LinearConfiguratorState,
  priceInfo?: PriceInfo | null,
): LinearConfiguratorApiCall {
  const data = prepareCommonConfiguratorData(linearState, priceInfo);

  const configuratorFamily = getConfiguratorFamilyFromProductCode(linearState.productCode);
  const t = (key: string) =>
    instance.t(key, { ns: XML_TRANSLATION_SHEETS[linearState.type!].sheetName, context: configuratorFamily });

  const linearData: UnitConfiguratorApiData = {
    // currentStepNumber is 1 based
    currentStepNumber: linearState.currentStep + 1,
    highestVisitedStepNumber: linearState.highTideStep + 1,
    customSegmentLengthSelected: linearState.customSegmentLengthSelected,
    customSecondSegmentLengthSelected: linearState.customSecondSegmentLengthSelected,
    resetCarriageDetails: linearState.resetCarriageDetails,
    enableCarriageEdit: linearState.enableCarriageEdit,
  };

  if (linearState.productCode === KLLT_UNIT_CONFIGURATOR_PRODUCT_CODE) {
    linearData.mediaUrl = t(linearState.productImageUrl);
    linearData.description = linearState.description;
  }

  data.carriagesConfiguratorData = linearData;

  const mainKmat = getMainKmat({ kmats: linearState.kmats });
  const lengthCstic = mainKmat?.cstics[FAKE_CSTIC_UNIT_LENGTH];
  if (lengthCstic && lengthCstic.value) {
    linearData.length = Number.parseFloat(lengthCstic.value);
  }

  data.linearConfiguratorData = linearData;

  return data as LinearConfiguratorApiCall;
}

const apiUrl = resolveApiUrl("/cpq/getconfig");

export function sendLinear(data: ConfiguratorApiCall): Promise<LinearConfiguration> {
  return fetch(apiUrl, {
    body: JSON.stringify(data),
    cache: "no-cache",
    credentials: "same-origin",
    method: "POST",
    mode: "cors",
    headers: {
      dataType: "json",
      "content-type": "application/json",
      CSRFToken: getCsrfValue(),
    },
    redirect: "follow",
  }).then((result) => {
    if (result.ok) {
      return result.json().then((data: LinearConfigurationDTO) => transform(data, ConfiguratorType.System, undefined));
    } else {
      console.error(`Error sending linear update: ${result.status}`);
      throw new Error(result.status + "");
    }
  });
}
